<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  metrics: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['refresh']);
const { t } = useI18n();

const windowHours = computed(() => props.metrics?.window_hours || 24);

const queueName = computed(() => props.metrics?.queue?.name || '-');
const queueSize = computed(() => props.metrics?.queue?.size ?? null);
const queueLatency = computed(() => props.metrics?.queue?.latency_sec ?? null);

const llmLatency = computed(() => props.metrics?.llm?.latency_ms || {});
const avgLatency = computed(() => Number(llmLatency.value?.avg || 0));
const maxLatency = computed(() => Number(llmLatency.value?.max || 0));
const costSum = computed(() =>
  Number(props.metrics?.llm?.estimated_cost_usd_sum || 0)
);
const totalTokens = computed(() =>
  Number(props.metrics?.llm?.total_tokens_sum || 0)
);

const handoffCount = computed(() => Number(props.metrics?.handoff?.count || 0));
const jobErrors = computed(() =>
  Array.isArray(props.metrics?.job_errors) ? props.metrics.job_errors : []
);
const jobErrorTotal = computed(() =>
  jobErrors.value.reduce((s, r) => s + Number(r.count || 0), 0)
);
</script>

<template>
  <CardLayout class="mb-5">
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1">
        <div class="text-sm font-medium text-n-slate-12">
          {{ t('AILAB.AI_HEALTH.TITLE') }}
        </div>
        <div class="text-xs text-n-slate-11">
          {{ t('AILAB.AI_HEALTH.SUBTITLE', { hours: windowHours }) }}
        </div>
      </div>
      <Button
        icon="i-lucide-refresh-cw"
        color="slate"
        variant="outline"
        size="sm"
        :label="t('AILAB.AI_HEALTH.REFRESH')"
        :is-loading="loading"
        :disabled="loading"
        @click="emit('refresh')"
      />
    </div>

    <div class="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
      <div class="p-3 rounded-lg bg-n-alpha-2">
        <div class="text-xs text-n-slate-11">
          {{ t('AILAB.AI_HEALTH.LLM_LATENCY_AVG') }}
        </div>
        <div class="text-lg font-medium text-n-slate-12">
          {{ avgLatency ? `${avgLatency.toFixed(1)} ms` : '-' }}
        </div>
        <div class="text-xs text-n-slate-11">
          {{
            t('AILAB.AI_HEALTH.LLM_LATENCY_MAX', {
              ms: maxLatency ? maxLatency.toFixed(1) : '-',
            })
          }}
        </div>
      </div>

      <div class="p-3 rounded-lg bg-n-alpha-2">
        <div class="text-xs text-n-slate-11">
          {{ t('AILAB.AI_HEALTH.COST_SUM') }}
        </div>
        <div class="text-lg font-medium text-n-slate-12">
          {{ costSum ? `$${costSum.toFixed(4)}` : '$0.0000' }}
        </div>
        <div class="text-xs text-n-slate-11">
          {{ t('AILAB.AI_HEALTH.TOKENS_SUM', { tokens: totalTokens }) }}
        </div>
      </div>

      <div class="p-3 rounded-lg bg-n-alpha-2">
        <div class="text-xs text-n-slate-11">
          {{ t('AILAB.AI_HEALTH.HANDOFF_COUNT') }}
        </div>
        <div class="text-lg font-medium text-n-slate-12">
          {{ handoffCount }}
        </div>
        <div class="text-xs text-n-slate-11">
          {{
            t('AILAB.AI_HEALTH.JOB_ERRORS_TOTAL', { count: jobErrorTotal })
          }}
        </div>
      </div>
    </div>

    <div class="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
      <div class="p-3 rounded-lg bg-n-alpha-2">
        <div class="text-sm font-medium text-n-slate-12">
          {{ t('AILAB.AI_HEALTH.QUEUE_TITLE', { queue: queueName }) }}
        </div>
        <div class="mt-2 flex items-center justify-between">
          <div class="text-xs text-n-slate-11">
            {{ t('AILAB.AI_HEALTH.QUEUE_SIZE') }}
          </div>
          <div class="text-sm font-medium text-n-slate-12">
            {{ queueSize ?? '-' }}
          </div>
        </div>
        <div class="mt-1 flex items-center justify-between">
          <div class="text-xs text-n-slate-11">
            {{ t('AILAB.AI_HEALTH.QUEUE_LATENCY') }}
          </div>
          <div class="text-sm font-medium text-n-slate-12">
            {{ queueLatency ?? '-' }}
          </div>
        </div>
      </div>

      <div class="p-3 rounded-lg bg-n-alpha-2">
        <div class="text-sm font-medium text-n-slate-12">
          {{ t('AILAB.AI_HEALTH.TOP_JOB_ERRORS') }}
        </div>
        <div v-if="!jobErrors.length" class="mt-1 text-sm text-n-slate-11">
          {{ t('AILAB.AI_HEALTH.NO_JOB_ERRORS') }}
        </div>
        <div v-else class="mt-2 flex flex-col gap-2">
          <div
            v-for="row in jobErrors.slice(0, 5)"
            :key="row.name"
            class="flex items-start justify-between gap-3"
          >
            <div class="text-xs text-n-slate-11 line-clamp-1" :title="row.name">
              {{ row.name }}
            </div>
            <div class="text-xs font-medium text-n-slate-12 shrink-0">
              {{ row.count }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
